/**
 * Copy and refusal vocabulary for `/stablecoins/invoices/settings`
 * (page spec `invoicing-settings`, ALL-5908).
 *
 * Every refusal sentence here names a code the merged issuer contract and the
 * invoicing client (`src/features/invoicing/api.ts`) can actually send — the
 * `Problem.code` values the contract declares (NOT_FOUND, VALIDATION_FAILED,
 * IDEMPOTENCY_KEY_REUSED, FORBIDDEN, DEPENDENCY_UNAVAILABLE), the
 * `VERSION_CONFLICT` the settings PUT documents for a stale `version`, and the
 * two client-side codes the api module mints itself when no response can be
 * read (`NETWORK_ERROR`, `MALFORMED_RESPONSE`).
 *
 * A code outside that list is never given an invented sentence: the page shows
 * the code itself, bounded, beside the support reference. Guessing copy for a
 * refusal we cannot name is how a real failure gets described as something it
 * is not.
 */

export const SETTINGS_COPY = {
  title: "Invoicing settings",
  subtitle:
    "Collection wallet per network, default terms and the billing profile invoices are issued under.",
  loadingLabel: "Loading invoicing settings",
  /** The billing profile card. The legal name is the org's KYB record. */
  billingProfileHeading: "Billing profile",
  billingProfileNote:
    "The legal name comes from this organization's KYB verification. It is read-only here and changes only through a new KYB review.",
  legalNameLabel: "Legal name",
  legalNameSource: "From KYB verification",
  enabledLabel: "Invoicing enabled",
  enabledOn: "Enabled",
  enabledOff: "Not enabled",
  numberPrefixLabel: "Invoice number prefix",
  retentionLabel: "Retention",
  retentionValue: (years: number) => `${years} year${years === 1 ? "" : "s"}`,
  taxIdsHeading: "Tax identifiers",
  billingAddressHeading: "Billing address",
  /** The terms card. */
  termsHeading: "Default terms",
  termsNote:
    "Applied to a new invoice unless the invoice overrides it. The expiry grace is bounded by the service; a value it will not accept is refused, never adjusted here.",
  defaultTermsLabel: "Default terms (days)",
  expiryGraceLabel: "Expiry grace (days)",
  reminderScheduleLabel: "Reminder schedule (days before due)",
  /** The collection wallet card. */
  walletsHeading: "Collection wallets",
  walletsNote:
    "One wallet per network and asset. Deposit addresses are derived under the wallet named here.",
  walletsEmptyTitle: "No collection wallet is set",
  walletsEmptyBody:
    "A collection wallet per network and asset is required before invoicing can be enabled. Nothing is configured for this organization yet.",
  walletNetworkColumn: "Network",
  walletAssetColumn: "Asset",
  walletIdColumn: "Collection wallet",
  walletReadinessColumn: "Readiness",
  walletCertified: "Certified",
  /** The readiness the contract expresses by the absence of `validatedAt`. */
  walletNotCertified: "Not certified",
  walletNotCertifiedHint:
    "The wallet has not been certified for this network, so invoicing cannot be enabled on it.",
  /** The limits card. */
  limitsHeading: "Network limits",
  limitsNote:
    "Amounts are entered in the asset's own units and sent as base units, exactly as the ledger holds them.",
  dustLabel: "Dust threshold",
  minInvoiceLabel: "Minimum invoice amount",
  limitsEmptyTitle: "No network limits are set",
  limitsEmptyBody: "No per-network floor is configured for this organization yet.",
  /** The editor. */
  editHeading: "Edit settings",
  saveLabel: "Save settings",
  savingLabel: "Saving…",
  cancelLabel: "Discard changes",
  unsavedHint: "Changes are not saved until you save them.",
  noChangesHint: "No changes to save.",
  /** Neither data nor a refusal came back: say so rather than render a default. */
  noDataBody:
    "The settings service answered without a configuration for this organization, so no wallet, term or billing profile is shown.",
  savedToast: "Invoicing settings saved.",
  /** Read-only mode when the loaded session does not grant settings management. */
  readonlyHint:
    "You can view these settings. Editing requires the invoice:settings.manage permission.",
  /** Field-level validation the client can do without guessing at the service's rules. */
  errorsHeading: "These fields need fixing",
  errorNotWholeNumber: "Enter a whole number.",
  errorEmptyAmount: "Enter an amount.",
  /** Refusal copy. Keys are the codes the settings routes can answer with. */
  refusals: {
    FORBIDDEN:
      "This organization does not allow you to change invoicing settings. An organization admin can grant invoice:settings.manage.",
    VALIDATION_FAILED:
      "The service refused one of these values. Nothing was saved — correct the value and save again.",
    VERSION_CONFLICT:
      "These settings changed since this page loaded, so your save was refused. Reload to see the current values before saving again.",
    IDEMPOTENCY_KEY_REUSED:
      "This save was refused because the same request key arrived with a different body. Reload and save once.",
    DEPENDENCY_UNAVAILABLE:
      "A service these settings depend on did not answer. Nothing was saved; try again.",
    NOT_FOUND: "The settings record could not be found for this organization.",
    NETWORK_ERROR:
      "The settings service could not be reached, so no value on this page is live. Nothing was saved.",
    MALFORMED_RESPONSE:
      "The settings service answered with a body this client could not read. Nothing on this page can be trusted until it answers correctly.",
  } as Record<string, string>,
} as const;

/**
 * True for a code that means "you may not do this", as opposed to "it failed".
 * Kept local so the page does not lean on `lib/api-failure.ts`'s set, which
 * does not carry the issuer contract's own `FORBIDDEN` code.
 */
export function isDeniedCode(code: string): boolean {
  return code === "FORBIDDEN" || code === "HTTP_401" || code === "HTTP_403";
}
